import React, { useState, useEffect } from 'react';
import { useAdminApi } from '../../hooks/useAdminApi';
import {
  X,
  Server,
  CheckCircle,
  AlertCircle,
  Loader2,
  Globe,
  Database,
  Key,
  Copy,
  ExternalLink,
  AlertTriangle
} from 'lucide-react';

const ProvisionTenantModal = ({ tenant, onClose, onSave }) => {
  const { post, put, get, loading } = useAdminApi();
  const [provisionStatus, setProvisionStatus] = useState(null);
  const [provisioning, setProvisioning] = useState(false);
  const [error, setError] = useState('');
  const [licenseKey, setLicenseKey] = useState('');
  const [copied, setCopied] = useState(false);
  const [customDomainConfigured, setCustomDomainConfigured] = useState(tenant?.domain?.isConfigured || false);
  const [savingDomain, setSavingDomain] = useState(false);

  useEffect(() => {
    if (tenant) {
      fetchProvisionStatus();
    }
  }, [tenant]);

  const fetchProvisionStatus = async () => {
    try {
      const response = await get(`/tenants/${tenant._id}/provision-status`);
      if (response.success) {
        setProvisionStatus(response.status);
        if (response.licenseKey) {
          setLicenseKey(response.licenseKey);
        }
      }
    } catch (err) {
      console.error('Failed to fetch provision status:', err);
    }
  };

  const handleProvision = async () => {
    setError('');
    setProvisioning(true);

    try {
      const response = await put(`/tenants/${tenant._id}/provision`, {});
      if (response.success) {
        setProvisionStatus(response.status);
        if (response.licenseKey) {
          setLicenseKey(response.licenseKey);
        }
        onSave();
      } else {
        setError(response.error || 'Provisioning failed');
      }
    } catch (err) {
      setError(err.message || 'An error occurred during provisioning');
    } finally {
      setProvisioning(false);
    }
  };

  const handleCopyLicense = () => {
    navigator.clipboard.writeText(licenseKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCustomDomainToggle = async () => {
    setSavingDomain(true);
    try {
      const newConfiguredState = !customDomainConfigured;
      const response = await put(`/tenants/${tenant.tenantId || tenant._id}`, {
        domain: {
          ...tenant.domain,
          isConfigured: newConfiguredState,
          configuredAt: newConfiguredState ? new Date().toISOString() : null
        }
      });
      if (response.success) {
        setCustomDomainConfigured(newConfiguredState);
      } else {
        setError(response.error || 'Failed to update domain configuration');
      }
    } catch (err) {
      setError(err.message || 'Failed to update domain configuration');
    } finally {
      setSavingDomain(false);
    }
  };

  const getStepStatus = (stepName) => {
    if (!provisionStatus) return 'pending';
    const step = provisionStatus.steps?.find(s => s.name === stepName);
    return step?.status || 'pending';
  };

  const StepItem = ({ icon: Icon, title, description, stepName }) => {
    const status = getStepStatus(stepName);
    const statusStyles = {
      completed: 'border-green-200 bg-green-50',
      in_progress: 'border-purple-200 bg-purple-50',
      failed: 'border-red-200 bg-red-50',
      pending: 'border-gray-200 bg-gray-50'
    };
    const iconStyles = {
      completed: 'text-green-600',
      in_progress: 'text-purple-600',
      failed: 'text-red-600',
      pending: 'text-gray-400'
    };

    return (
      <div className={`flex items-start p-4 border rounded-xl ${statusStyles[status]}`}>
        <div className={`flex-shrink-0 ${iconStyles[status]}`}>
          {status === 'completed' ? (
            <CheckCircle className="w-6 h-6" />
          ) : status === 'in_progress' ? (
            <Loader2 className="w-6 h-6 animate-spin" />
          ) : status === 'failed' ? (
            <AlertCircle className="w-6 h-6" />
          ) : (
            <Icon className="w-6 h-6" />
          )}
        </div>
        <div className="ml-4">
          <h4 className="font-medium text-gray-900">{title}</h4>
          <p className="text-sm text-gray-500 mt-0.5">{description}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full mx-auto z-10">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center mr-3">
                <Server className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Provision Tenant</h2>
                <p className="text-sm text-gray-500">{tenant?.companyName}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start">
                <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Tenant Info */}
            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Subdomain</p>
                  <p className="font-medium text-gray-900">
                    {tenant?.domain?.subdomain || tenant?.subdomain}.bestohrms.com
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Plan</p>
                  <p className="font-medium text-gray-900 capitalize">
                    {tenant?.subscription?.plan || tenant?.plan}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Email</p>
                  <p className="font-medium text-gray-900">{tenant?.email}</p>
                </div>
                <div>
                  <p className="text-gray-500">Status</p>
                  <p className="font-medium text-gray-900 capitalize">
                    {tenant?.provisioningStatus || tenant?.status}
                  </p>
                </div>
              </div>
            </div>

            {/* Custom Domain Section */}
            {tenant?.domain?.type === 'custom' && tenant?.domain?.customDomain && (
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start">
                    <Globe className="w-5 h-5 text-purple-600 mt-0.5 mr-3" />
                    <div>
                      <h4 className="font-medium text-purple-900">Custom Domain Requested</h4>
                      <p className="text-sm text-purple-700 mt-1">
                        {tenant.domain.customDomain}
                      </p>
                    </div>
                  </div>
                  {!customDomainConfigured && (
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-amber-100 text-amber-700">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      Pending Setup
                    </span>
                  )}
                  {customDomainConfigured && (
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Configured
                    </span>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t border-purple-200">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={customDomainConfigured}
                      onChange={handleCustomDomainToggle}
                      disabled={savingDomain}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                    <span className="ml-2 text-sm text-purple-800">
                      {savingDomain ? 'Saving...' : 'Mark custom domain as configured'}
                    </span>
                  </label>
                  <p className="mt-2 text-xs text-purple-600">
                    Check this after you have configured DNS and SSL for the custom domain.
                  </p>
                </div>
              </div>
            )}

            {/* Provisioning Steps */}
            <div className="space-y-3 mb-6">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                Provisioning Steps
              </h3>
              <StepItem
                icon={Globe}
                title="DNS Configuration"
                description="Create subdomain and DNS records"
                stepName="dns"
              />
              <StepItem
                icon={Database}
                title="Database Setup"
                description="Initialize tenant database and collections"
                stepName="database"
              />
              <StepItem
                icon={Key}
                title="License Generation"
                description="Generate and assign license key"
                stepName="license"
              />
            </div>

            {/* License Key Display */}
            {licenseKey && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-green-800">License Key Generated</h4>
                  <button
                    onClick={handleCopyLicense}
                    className="flex items-center text-green-600 hover:text-green-700 text-sm font-medium"
                  >
                    {copied ? (
                      <>
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-1" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
                <code className="block text-sm text-green-700 font-mono bg-green-100 p-2 rounded-lg break-all">
                  {licenseKey}
                </code>
              </div>
            )}

            {/* Access Link */}
            {provisionStatus?.completed && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                <h4 className="font-medium text-blue-800 mb-2">Tenant Portal Ready</h4>
                {tenant?.domain?.type === 'custom' && customDomainConfigured && tenant?.domain?.customDomain ? (
                  <a
                    href={`https://${tenant.domain.customDomain}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    https://{tenant.domain.customDomain}
                  </a>
                ) : (
                  <a
                    href={`https://${tenant?.domain?.subdomain || tenant?.subdomain}.bestohrms.com`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    https://{tenant?.domain?.subdomain || tenant?.subdomain}.bestohrms.com
                  </a>
                )}
              </div>
            )}

            {/* Action Button */}
            {!provisionStatus?.completed && (
              <button
                onClick={handleProvision}
                disabled={provisioning}
                className="w-full flex items-center justify-center px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl hover:from-blue-700 hover:to-cyan-700 transition-colors disabled:opacity-50"
              >
                {provisioning ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Provisioning...
                  </>
                ) : (
                  <>
                    <Server className="w-5 h-5 mr-2" />
                    Start Provisioning
                  </>
                )}
              </button>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2.5 text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProvisionTenantModal;
